import React, { useEffect, useState } from "react";
import {
  getRolesByApplicationId,
  getRolesByApplicationIdAndCustomerIdAndUserId,
  Role,
} from "@/api/roles";
import { Application, getApplication } from "@/api/applications";
import { Customer, getCustomer } from "@/api/customers";
import { getUser, User } from "@/api/users";
import AddButton from "@/ui-controls/AddButton";
import AppCustomerUserRoleDetailList from "@/components/roles/appCustomerUserRoles/AppCustomerUserRoleDetailList";
import NewAppCustomerUserRolePanel from "@/components/roles/appCustomerUserRoles/NewAppCustomerUserRolePanel";
import SearchBox from "@/ui-controls/SearchBox";
import { IconButton, MessageBar, MessageBarType } from "@fluentui/react";
import { useNavigate } from "react-router-dom";
import { useErrorNotification } from "@/utils/useErrorNotification";

interface Props {
  ApplicationId: number;
  CustomerId: number;
  UserId: number;
  ShowOnlyNewAppUserRolePanel: boolean;
  onCloseNewAppUserRolePanel?: () => any;
  onSaveNewAppRole?: (role: Role) => any;
  onAddUserRoles?: (roles: Role[]) => any;
  onUserLoaded?: (email: string) => void;
  breadcrumbTitle?: string;
  breadcrumbSubtitle?: React.ReactNode;
  backPath?: string;
}

const AppUserRoles = (props: Props) => {
  const navigate = useNavigate();
  const { errorMessage, showError, clearError } = useErrorNotification();
  const [applicationCustomerUserRoles, setApplicationCustomerUserRoles] =
    useState<Role[]>([]);
  const [applicationRoles, setApplicationRoles] = useState<Role[]>([]);
  const [unassignedAppRoles, setUnassignedAppRoles] = useState<Role[]>([]);
  const [application, setApplication] = useState<Application | undefined>(
    undefined
  );
  const [customer, setCustomer] = useState<Customer | undefined>(undefined);
  const [user, setUser] = useState<User | undefined>(undefined);
  const [
    loadingApplicationCustomerUserRoles,
    setLoadingApplicationCustomerUserRoles,
  ] = useState<boolean>(false);
  const [loadingAppRoles, setLoadingAppRoles] = useState<boolean>(false);
  const [loadingApplication, setLoadingApplication] = useState<boolean>(false);
  const [loadingCustomer, setLoadingCustomer] = useState<boolean>(false);
  const [loadingUser, setLoadingUser] = useState<boolean>(false);
  const [
    newAppCustomerUserRolePanelOpenned,
    setNewAppCustomerUserRolePanelOpenned,
  ] = useState<boolean>(props.ShowOnlyNewAppUserRolePanel);
  const [filteredUserRoles, setFilteredUserRoles] = useState<Role[]>([]);
  const [isSearchPerformed, setIsSearchPerformed] = useState<boolean>(false);

  useEffect(() => {
    if (props.ApplicationId && props.CustomerId && props.UserId) {
      getApplicationCustomerUserRoles(
        props.ApplicationId,
        props.CustomerId,
        props.UserId
      );
    }
  }, [props.ApplicationId, props.CustomerId, props.UserId]);

  useEffect(() => {
    if (props.UserId) {
      getUserById(props.UserId);
    }
  }, [props.UserId]);

  useEffect(() => {
    if (props.CustomerId) {
      getCustomerById(props.CustomerId);
    }
  }, [props.CustomerId]);

  useEffect(() => {
    if (props.ApplicationId) {
      getApplicatioRoles(props.ApplicationId);
      getApplicationById(props.ApplicationId);
    }
  }, [props.ApplicationId]);

  useEffect(() => {
    getUnassignedAppRoles();
  }, [applicationRoles, applicationCustomerUserRoles]);

  const getApplicatioRoles = (applicationId: number) => {
    setLoadingAppRoles(true);
    getRolesByApplicationId(applicationId)
      .then((result) => {
        setApplicationRoles(result);
        setLoadingAppRoles(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingAppRoles(false);
      });
  };

  const getApplicationById = (applicationId: number) => {
    setLoadingApplication(true);
    getApplication(applicationId)
      .then((result) => {
        setApplication(result);
        setLoadingApplication(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingApplication(false);
      });
  };

  const getCustomerById = (customerId: number) => {
    setLoadingCustomer(true);
    getCustomer(customerId)
      .then((result) => {
        setCustomer(result);
        setLoadingCustomer(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingCustomer(false);
      });
  };

  const getApplicationCustomerUserRoles = (
    applicationId: number,
    customerId: number,
    userId: number
  ) => {
    setLoadingApplicationCustomerUserRoles(true);
    getRolesByApplicationIdAndCustomerIdAndUserId(
      applicationId,
      customerId,
      userId
    )
      .then((result) => {
        setApplicationCustomerUserRoles(result);
        setLoadingApplicationCustomerUserRoles(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingApplicationCustomerUserRoles(false);
      });
  };

  const getUserById = (userId: number) => {
    setLoadingUser(true);
    getUser(userId)
      .then((result) => {
        setUser(result);
        if (props.onUserLoaded && result?.EmailAddress) {
          props.onUserLoaded(result.EmailAddress);
        }
        setLoadingUser(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingUser(false);
      });
  };

  const getUnassignedAppRoles = () => {
    const roles = applicationRoles.filter(
      (ar) => !applicationCustomerUserRoles.find((ur) => ur.Id === ar.Id)
    );
    setUnassignedAppRoles(roles);
  };

  const handleError = (error: any) => {
    showError(error);
  };

  const handleRoleSearch = (searchValue: string) => {
    const hasSearchValue = searchValue.trim().length > 0;
    setIsSearchPerformed(hasSearchValue);

    if (hasSearchValue) {
      const filteredRoles = applicationCustomerUserRoles.filter(
        (role) =>
          role.Name.toLowerCase().includes(searchValue.toLowerCase()) ||
          (role.Description &&
            role.Description.toLowerCase().includes(searchValue.toLowerCase()))
      );
      setFilteredUserRoles(filteredRoles);
    } else {
      setFilteredUserRoles([]);
    }
  };

  const handleRefresh = () => {
    setIsSearchPerformed(false);
    setFilteredUserRoles([]);
    getApplicationCustomerUserRoles(
      props.ApplicationId,
      props.CustomerId,
      props.UserId
    );
  };

  return (
    <>
      {!props.ShowOnlyNewAppUserRolePanel ? (
        <>
          <div className="page-header">
            <div className="breadcrumb-left-section">
              {props.backPath && (
                <IconButton
                  iconProps={{ iconName: "Back" }}
                  title="Back to Users"
                  onClick={() => navigate(props.backPath!)}
                  styles={{
                    root: { width: 40, height: 40 },
                    rootHovered: { backgroundColor: "#f3f2f1" },
                    icon: { fontSize: 18, color: "#0078d4" },
                    rootPressed: { backgroundColor: "#e1dfdd" },
                  }}
                />
              )}
              <div>
                {props.breadcrumbTitle && (
                  <h2 className="page-breadcrumb-title">
                    {props.breadcrumbTitle}
                  </h2>
                )}
                {props.breadcrumbSubtitle && (
                  <div style={{ color: "#605e5c", fontSize: "14px" }}>
                    {props.breadcrumbSubtitle}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <AddButton
                text="Role"
                onClick={() => setNewAppCustomerUserRolePanelOpenned(true)}
              />
              <SearchBox placeholder="Search" onSearch={handleRoleSearch} />
              <IconButton
                iconProps={{ iconName: "Refresh" }}
                title="Refresh"
                onClick={handleRefresh}
                styles={{
                  root: { width: 40, height: 40 },
                  rootHovered: { backgroundColor: "#f3f2f1" },
                  icon: { fontSize: 18, color: "#0078d4" },
                  rootPressed: { backgroundColor: "#e1dfdd" },
                }}
              />
            </div>
          </div>
          <div>
            {application && customer && user ? (
              <AppCustomerUserRoleDetailList
                application={application}
                customer={customer}
                user={user}
                roleList={
                  isSearchPerformed
                    ? filteredUserRoles.length === 0
                      ? []
                      : filteredUserRoles
                    : applicationCustomerUserRoles
                }
                onRemoveRoles={() =>
                  getApplicationCustomerUserRoles(
                    props.ApplicationId,
                    props.CustomerId,
                    props.UserId
                  )
                }
              />
            ) : null}
          </div>
        </>
      ) : null}
      <NewAppCustomerUserRolePanel
        application={application}
        customer={customer}
        user={user}
        appRoles={applicationRoles}
        unassignedAppRoles={unassignedAppRoles}
        isPanelOpenned={newAppCustomerUserRolePanelOpenned}
        onPanelClosed={() => {
          setNewAppCustomerUserRolePanelOpenned(false);
          if (props.onCloseNewAppUserRolePanel)
            props.onCloseNewAppUserRolePanel();
        }}
        onAddApplicationCustomerUserRole={(model) => {
          getApplicationCustomerUserRoles(
            props.ApplicationId,
            props.CustomerId,
            props.UserId
          );
        }}
      />
      {errorMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={false}
          onDismiss={clearError}
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 1000,
            },
          }}
        >
          {errorMessage}
        </MessageBar>
      )}
    </>
  );
};

export default AppUserRoles;
